import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Menu } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import ConversationList from '../components/ConversationList';
import ConversationDetail from '../components/ConversationDetail';
import WhatsAppSetup from '../components/WhatsAppSetup';
import UserManagement from '../components/UserManagement';
import DepartmentManagement from '../components/DepartmentManagement';
import MetricsDashboard from '../components/MetricsDashboard';
import ContactsManagement from '../components/ContactsManagement';
import QuickRepliesManagement from '../components/QuickRepliesManagement';
import TagsManagement from '../components/TagsManagement';
import ChatbotSettings from '../components/ChatbotSettings';
import BusinessHours from '../components/BusinessHours';
import ScheduledMessages from '../components/ScheduledMessages';
import AgentOverview from '../components/AgentOverview';
import socketService from '../services/socketService';
import api from '../services/api';
import '../styles/Dashboard.css';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

function Dashboard() {
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState('conversations');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [unreadConvs, setUnreadConvs] = useState(() => new Set());
  const [unreadGroups, setUnreadGroups] = useState(() => new Set());
  // Mantém também split por status pra ConversationList saber se uma nova mensagem
  // caiu em Aguardando (pending) ou Atendendo (open).
  const [unreadByStatus, setUnreadByStatus] = useState({ pending: new Set(), open: new Set() });
  const navigate = useNavigate();

  // Refs pra socket callback ler sem re-subscrever
  const selectedRef = useRef(null);
  const sectionRef = useRef('conversations');
  useEffect(() => { selectedRef.current = selectedConversation; }, [selectedConversation]);
  useEffect(() => { sectionRef.current = activeSection; }, [activeSection]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) { navigate('/login'); return; }
    const pu = JSON.parse(storedUser); setUser(pu); socketService.connect(pu.tenant_id);

    // Registrar Push Notifications
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return;
      try {
        const reg = await navigator.serviceWorker.ready;
        const { data } = await api.get('/push/vapid-public-key');
        const keyBytes = urlBase64ToUint8Array(data.key);
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes });
        await api.post('/push/subscribe', sub.toJSON());
      } catch (e) { /* silent */ }
    })();

    // Busca status atual ao carregar (não espera evento de socket)
    api.get('/whatsapp/connection')
      .then(r => setWhatsappStatus(r.data.connected ? 'connected' : 'disconnected'))
      .catch(() => {});

    // Reidrata badges de não-lidas a partir do estado persistente do servidor
    // (sobrevive a F5 — antes era só Set em memória React).
    const seedUnread = () => api.get('/conversations/unread/summary')
      .then(r => {
        const { groups = [], pending = [], open = [] } = r.data || {};
        setUnreadGroups(new Set(groups));
        setUnreadConvs(new Set([...pending, ...open]));
        setUnreadByStatus({
          pending: new Set(pending),
          open: new Set(open)
        });
      })
      .catch(() => {});
    seedUnread();
    // Refresca também quando reconectar o socket (caso tenhamos perdido eventos)
    socketService.on('connect', seedUnread);

    const onWaStatus = (data) => setWhatsappStatus(data.connected ? 'connected' : 'disconnected');
    const onPresence = (data) => setOnlineUserIds((data.onlineUserIds || []).map(String));

    // Badges de não-lidas no Sidebar — separa por aba (Atendimentos vs Grupos)
    const onMessageNew = (data) => {
      if (data.senderType !== 'client') return;
      const convId = data.conversationId;
      const isGroup = !!data.isGroup;

      // Se o usuário está olhando exatamente essa conversa na aba certa (e a janela está visível), não conta.
      const sel = selectedRef.current;
      const sec = sectionRef.current;
      const sameSection = (isGroup && sec === 'groups') || (!isGroup && sec === 'conversations');
      if (sel && sel.id === convId && sameSection && document.visibilityState === 'visible') return;

      if (isGroup) {
        setUnreadGroups(prev => {
          if (prev.has(convId)) return prev;
          const next = new Set(prev); next.add(convId); return next;
        });
      } else {
        setUnreadConvs(prev => {
          if (prev.has(convId)) return prev;
          const next = new Set(prev); next.add(convId); return next;
        });
        // Split por status: 'pending' (Aguardando) ou 'open' (Atendendo).
        // Resolvidos ('closed') não contam como unread.
        const status = data.convStatus === 'open' ? 'open'
                     : data.convStatus === 'pending' ? 'pending'
                     : null;
        if (status) {
          setUnreadByStatus(prev => {
            if (prev[status].has(convId)) return prev;
            const otherKey = status === 'open' ? 'pending' : 'open';
            const nextSet = new Set(prev[status]); nextSet.add(convId);
            // Remove do outro status caso já estivesse lá (ex: pending → open após aceite)
            const otherSet = new Set(prev[otherKey]); otherSet.delete(convId);
            return { [status]: nextSet, [otherKey]: otherSet };
          });
        }
      }
    };

    // Conversa lida (em qualquer sessão/dispositivo) → limpa badges locais
    const onConversationRead = (data) => {
      const id = data.conversationId;
      if (!id) return;
      setUnreadConvs(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev); next.delete(id); return next;
      });
      setUnreadGroups(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev); next.delete(id); return next;
      });
      setUnreadByStatus(prev => {
        if (!prev.pending.has(id) && !prev.open.has(id)) return prev;
        const pendingSet = new Set(prev.pending); pendingSet.delete(id);
        const openSet = new Set(prev.open); openSet.delete(id);
        return { pending: pendingSet, open: openSet };
      });
    };

    socketService.on('whatsapp:status', onWaStatus);
    socketService.on('presence:update', onPresence);
    socketService.on('message:new', onMessageNew);
    socketService.on('conversation:read', onConversationRead);

    return () => {
      socketService.off('whatsapp:status', onWaStatus);
      socketService.off('presence:update', onPresence);
      socketService.off('message:new', onMessageNew);
      socketService.off('conversation:read', onConversationRead);
      socketService.off('connect', seedUnread);
      socketService.disconnect();
    };
  }, [navigate]);

  // Ao abrir uma conversa, retira o id dos sets de não-lidas (de ambos pra ser seguro)
  useEffect(() => {
    if (!selectedConversation?.id) return;
    const id = selectedConversation.id;
    setUnreadConvs(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev); next.delete(id); return next;
    });
    setUnreadGroups(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev); next.delete(id); return next;
    });
    setUnreadByStatus(prev => {
      const pendingSet = new Set(prev.pending); pendingSet.delete(id);
      const openSet = new Set(prev.open); openSet.delete(id);
      return { pending: pendingSet, open: openSet };
    });
  }, [selectedConversation]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (!user) return (
    <div className="app-loading">
      <MessageSquare size={20} />
      Carregando...
    </div>
  );
  const isAdmin = user.role === 'admin';

  const handleSelectSection = (section) => {
    // Trocando entre 'conversations' e 'groups' (ou saindo) limpa a conversa aberta,
    // pra não vermos um chat individual na tela de grupos ou vice-versa.
    if (section !== activeSection) setSelectedConversation(null);
    setActiveSection(section);
    setSidebarOpen(false);
  };

  return (
    <div className="dashboard">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <Sidebar
        user={user}
        activeSection={activeSection}
        onSelectSection={handleSelectSection}
        onLogout={handleLogout}
        whatsappStatus={whatsappStatus}
        onClose={() => setSidebarOpen(false)}
        className={sidebarOpen ? 'sidebar-mobile-open' : ''}
        unreadCounts={{ conversations: unreadConvs.size, groups: unreadGroups.size }}
      />

      <main className="dashboard-content">
        {/* Mobile hamburger — escondido quando uma conversa está aberta no mobile */}
        <button
          className={`hamburger-btn ${activeSection === 'conversations' && selectedConversation ? 'hamburger-hidden-mobile' : ''}`}
          onClick={() => setSidebarOpen(true)}
          title="Menu"
        >
          <Menu size={20} />
        </button>

        {(activeSection === 'conversations' || activeSection === 'groups') && (
          <div className={`conversations-area ${selectedConversation ? 'has-detail' : ''}`}>
            <ConversationList
              key={activeSection}
              isGroup={activeSection === 'groups'}
              onSelectConversation={setSelectedConversation}
              selectedId={selectedConversation?.id}
              unreadByTab={{
                pending: unreadByStatus.pending.size,
                open: unreadByStatus.open.size
              }}
            />
            {selectedConversation ? (
              <ConversationDetail
                conversation={selectedConversation}
                onBack={() => setSelectedConversation(null)}
                onStartConversation={setSelectedConversation}
              />
            ) : (
              <div className="chat-empty-state desktop-only">
                <div className="chat-empty-icon">
                  <MessageSquare size={32} />
                </div>
                <h3>Selecione um atendimento</h3>
                <p>Escolha uma conversa na lista ao lado para visualizar as mensagens e interagir com o cliente.</p>
              </div>
            )}
          </div>
        )}

        {activeSection === 'contacts' && (
          <ContactsManagement
            onStartConversation={(conv) => {
              setSelectedConversation(conv);
              setActiveSection('conversations');
            }}
          />
        )}
        {activeSection === 'quick-replies' && <QuickRepliesManagement />}
        {activeSection === 'scheduled' && <ScheduledMessages />}

        {isAdmin && activeSection === 'overview' && <AgentOverview onlineUserIds={onlineUserIds} />}
        {isAdmin && activeSection === 'metrics' && <MetricsDashboard />}
        {isAdmin && activeSection === 'tags' && <TagsManagement />}
        {isAdmin && activeSection === 'chatbot' && <ChatbotSettings />}
        {isAdmin && activeSection === 'hours' && <BusinessHours />}
        {isAdmin && activeSection === 'whatsapp' && <WhatsAppSetup tenantId={user.tenant_id} />}
        {isAdmin && activeSection === 'departments' && <DepartmentManagement tenantId={user.tenant_id} />}
        {isAdmin && activeSection === 'users' && <UserManagement tenantId={user.tenant_id} />}
      </main>
    </div>
  );
}

export default Dashboard;
